import { ContentLinkOptions, useContentLink, usePageContent, useSubCategories } from "@robe/client/xcore-ui";
import { Content, ContentsResponse, PickContentValues, sentryLogger } from "@robe/core";
import { OptionType } from "design-system/xcore/Select";
import { getQueryValueFromFilterOptions, useSubCategoryFilter } from "hooks";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { OnChangeValue } from "react-select";
import { useLayout } from "xcore";
import { Filter, FilterQueryObject, NewsPreview, SupportedNewsQueryKeys } from "./types";
import { MAX_NEWS_CARDS_COUNT_PER_PAGE } from "./utils";

interface GetQueryProps {
  key?: SupportedNewsQueryKeys;
  values?: OptionType<number>[];
}

/**
 * Resolve filters to query params object available in `nextPage()`
 * and router replace event with these query params in `onChange()`.
 * */
const useFilters = (filters: Filter[], trans?: ContentLinkOptions & { shallow?: boolean }) => {
  const { query } = useRouter();
  const content = usePageContent();
  const { replace } = useContentLink();

  // Build query params object with product, category and year filters
  const getQuery = ({ key, values }: GetQueryProps): FilterQueryObject => ({
    ...(Object.assign({}, ...filters.map(([_, getQ, k]) => (k === key ? getQ(values) : getQ()))) as FilterQueryObject),
  });

  const onChange = (key: SupportedNewsQueryKeys) => (valuesProvided: OnChangeValue<OptionType, boolean>) => {
    const values = ((valuesProvided as OptionType<number>[]) ?? []).filter((item) => !!item);

    // Keep query params and omit last added key. Also, always reset pagination (remove page param) on filters change
    let queryWithoutCurrentKey = query;
    if (key in query) {
      const { [key]: _removed, page: _pageRemoved, ...rest } = query;
      queryWithoutCurrentKey = rest;
    }

    // Product filter is handled externally in `NewsFilter`
    const queryValues = key === "product" ? values.map(({ value }) => value) : getQuery({ key, values })[key].map(({ value }) => value);

    sentryLogger({
      type: "debug",
      debugBlock: "filters",
      context: {
        key,
        values,
        valuesProvided,
        query,
        queryWithoutCurrentKey,
        queryValues,
        content,
      },
    });

    // Replace query params
    return replace(content, {
      queries: {
        [key]: queryValues,
        ...queryWithoutCurrentKey,
      },
      ...trans,
    });
  };

  return {
    /** Add / remove selected value in query params */
    onChange,
    /** Load more news and preserve current query params */
    nextPage: () => ({ ...query, page: Number(query.page ?? 1) + 1 }),
  };
};

/** Resolve values to query params object */
export const useValueFilter = (q: string, options: OptionType<number>[]) => {
  const { query } = useRouter();

  const value = useMemo(() => getQueryValueFromFilterOptions(query[q] ?? [], options), [options, q, query]);

  const getQuery = (v?: OptionType<number>[]) => ({ [q]: v ?? value });

  return [{ options, value }, getQuery, q] as const;
};

export const useProductsFilter = () => {
  const { query } = useRouter();

  if ("product" in query) {
    const productIds = Array.isArray(query.product) ? query.product : [query.product];

    const numericOnlyProductIds = productIds.filter((id) => !isNaN(Number(id))).map(Number);

    return numericOnlyProductIds;
  }

  return [];
};

export const useNewsFilters = (
  { pagination, contents }: Pick<ContentsResponse<NewsPreview>, "contents" | "pagination">,
  fetchedProducts: PickContentValues<Content, "discontinuedReg" | "discontinuedGlob">[]
) => {
  const { stringify, localize } = useLayout();
  const categories = useSubCategories();
  const productsIds = useProductsFilter();

  const sortedCategories = useMemo(
    () => [...categories[0]].filter(({ name }) => !!name).sort((a, b) => stringify(a.name).localeCompare(stringify(b.name), "en")),
    [categories, stringify]
  );

  const category = useSubCategoryFilter("category", sortedCategories);

  const years = useValueFilter(
    "year",
    [...Array(new Date().getFullYear() - 2001)]
      .map((_, i) => {
        const filterYear = 2002 + i;

        return { label: filterYear.toString(), value: filterYear };
      })
      .reverse()
  );

  const products = useValueFilter(
    "product",
    fetchedProducts
      .filter((x) => !x.values.discontinuedGlob && !localize(x.values.discontinuedReg) && x.title)
      .map((x) => ({ label: stringify(x.title), value: x.id }))
      .sort((x, y) => x.label.localeCompare(y.label))
  );

  const { nextPage, onChange } = useFilters([products, category, years], { shallow: true });

  const news = useMemo(
    () => {
      if (!pagination?.next) {
        return contents;
      }

      const newsFilteredByProducts = productsIds.length
        ? contents.filter((n) => n.values.products?.some(({ id }) => productsIds.includes(id)))
        : contents;

      const totalWidth = newsFilteredByProducts.reduce((acc, n) => acc + typeToWidth(n.values.layout), 0);
      /** There can be only 2, 3 or 4 cols in news grid */
      const toTrim = totalWidth % MAX_NEWS_CARDS_COUNT_PER_PAGE;

      let w = 0;
      const result: NewsPreview[] = [];

      for (const n of newsFilteredByProducts) {
        w += typeToWidth(n.values.layout);

        if (w > totalWidth - toTrim) {
          break;
        }

        result.push(n as NewsPreview);
      }

      return newsFilteredByProducts;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contents, pagination?.next, localize, stringify, productsIds]
  );

  return {
    category,
    years,
    products,
    onChange,
    nextPage,
    news,
  };
};

const typeToWidth = (type: string | undefined) => (type === "fullBackgroundAndWide" ? 2 : 1);
